/* eslint-disable @atlassian/i18n/enforce-id-by-package */

import { defineMessages } from 'react-intl';

type MessageDescriptor = {
	id: string;
	defaultMessage: string;
	description: string;
};

export const messages: {
	loginButton: MessageDescriptor;
	loginAriaLabel: MessageDescriptor;
	userAccountMenuAriaLabel: MessageDescriptor;
	teamworkGraphBrand: MessageDescriptor;
	navAbout: MessageDescriptor;
	navConnectors: MessageDescriptor;
	navCli: MessageDescriptor;
	navMcpPage: MessageDescriptor;
	navBenchmark: MessageDescriptor;
	navMcp: MessageDescriptor;
	navDocs: MessageDescriptor;
	navYourGraph: MessageDescriptor;
	navGettingStarted: MessageDescriptor;
	navPricing: MessageDescriptor;
	navMenuLabel: MessageDescriptor;
	navPagesHeading: MessageDescriptor;
	navSectionsHeading: MessageDescriptor;
	navOnThisPageHeading: MessageDescriptor;
	navSectionsLabel: MessageDescriptor;
	profileLabel: MessageDescriptor;
	logoutLabel: MessageDescriptor;
	giveFeedback: MessageDescriptor;
	logoAlt: MessageDescriptor;
} = defineMessages({
	loginButton: {
		id: 'twg-microsite.navbar.loginButton',
		defaultMessage: 'Log in',
		description: 'Button to log in to the application',
	},
	loginAriaLabel: {
		id: 'twg-microsite.navbar.loginAriaLabel',
		defaultMessage: 'Log in',
		description: 'Accessible label for the log in button',
	},
	userAccountMenuAriaLabel: {
		id: 'twg-microsite.navbar.userAccountMenuAriaLabel',
		defaultMessage: 'Account menu, {userName}',
		description:
			'Accessible label for the signed-in user avatar that opens the account menu. {userName} is the user display name.',
	},
	teamworkGraphBrand: {
		id: 'twg-microsite.navbar.teamworkGraphBrand',
		defaultMessage: 'Atlassian Teamwork Graph',
		description:
			'Atlassian Teamwork Graph product name shown in the nav bar. "Atlassian Teamwork Graph" is the full Atlassian product name.',
	},
	navAbout: {
		id: 'twg-microsite.navbar.navAbout',
		defaultMessage: 'About',
		description:
			'Navigation link used in two places: in the current nav bar it opens the microsite home page, which describes the product, and in the older nav bar it scrolls to the About section of that home page. The label is the same for signed-in and signed-out visitors.',
	},
	navConnectors: {
		id: 'twg-microsite.navbar.navConnectors',
		defaultMessage: 'Connectors',
		description: 'Navigation link to the Connectors section of the microsite',
	},
	navCli: {
		id: 'twg-microsite.navbar.navCli',
		defaultMessage: 'CLI',
		description:
			'Navigation link that opens the microsite page for the Teamwork Graph CLI. "CLI" means command-line interface, and "Teamwork Graph CLI" is an Atlassian product name.',
	},
	navMcpPage: {
		id: 'twg-microsite.navbar.navMcpPage.non-final',
		defaultMessage: 'MCP',
		description:
			'Navigation link that opens the microsite page for Teamwork Graph Model Context Protocol setup. "MCP" means Model Context Protocol, a protocol for connecting artificial intelligence applications to tools and context.',
	},
	navBenchmark: {
		id: 'twg-microsite.navbar.navBenchmark.non-final',
		defaultMessage: 'Benchmark',
		description: 'Navigation link that opens the Teamwork Graph benchmark page.',
	},
	navMcp: {
		id: 'twg-microsite.navbar.navMcp',
		defaultMessage: 'MCP',
		description:
			'Navigation link to the Getting Started section of the microsite, pre-selecting the MCP install instructions tab. "MCP" means Model Context Protocol, a protocol for connecting AI applications to tools and context.',
	},
	navDocs: {
		id: 'twg-microsite.navbar.navDocs',
		defaultMessage: 'Docs',
		description: 'Navigation link to the documentation section of the microsite',
	},
	navYourGraph: {
		id: 'twg-microsite.navbar.navYourGraphs',
		defaultMessage: 'Your Graph',
		description:
			'Navigation link for the signed-in visitor\'s own graph, used in two places: on the microsite home page it scrolls to the Your Graph section, and from another page it opens the home page scrolled to that section. Here, "Graph" means the user-specific connected data structure of entities and relationships, as in computer science, not a chart, plot, graphic, or visualization.',
	},
	navGettingStarted: {
		id: 'twg-microsite.navbar.navGettingStarted',
		defaultMessage: 'Getting started',
		description:
			'Section link in the nav bar\'s section menus that scrolls to the microsite home page\'s Getting Started section, which introduces the CLI and MCP setup options. "CLI" means command-line interface and "MCP" means Model Context Protocol, a protocol for connecting AI applications to tools and context. Distinct from the "CLI" page link, which opens the separate, dedicated CLI page rather than scrolling.',
	},
	navPricing: {
		id: 'twg-microsite.navbar.navPricing',
		defaultMessage: 'Pricing FAQ',
		description:
			'Section link in the nav bar\'s About menu that jumps to the Pricing FAQ section at the bottom of the microsite home page. It is an in-page hash link, not a dedicated pricing page. "FAQ" means frequently asked questions.',
	},
	navMenuLabel: {
		id: 'twg-microsite.navbar.navMenuLabel',
		defaultMessage: 'Open navigation menu',
		description:
			'Accessible name for the hamburger button that opens the collapsed navigation menu on narrow screens. The button itself shows only an icon, no visible text.',
	},
	navPagesHeading: {
		id: 'twg-microsite.navbar.navPagesHeading',
		defaultMessage: 'Pages',
		description:
			'Heading above the page links inside the collapsed hamburger navigation menu, distinguishing them from the current page\'s section links, which are listed under a separate heading below when the page has any. "CLI" means command-line interface.',
	},
	navSectionsHeading: {
		id: 'twg-microsite.navbar.navSectionsHeading',
		defaultMessage: 'Sections',
		description:
			'Heading above the list of links that jump to individual sections of the microsite home page, shown inside the dropdown attached to the "About" link in the navigation bar. Not "On this page": the dropdown is offered from every route, so from another page these are a different page\'s sections.',
	},
	navOnThisPageHeading: {
		id: 'twg-microsite.navbar.navOnThisPageHeading',
		defaultMessage: 'On this page',
		description:
			'Heading above the links that jump to sections of the page the visitor is currently on, shown inside the collapsed hamburger navigation menu on narrow screens. Only ever shown on a page that has such sections, so "this page" is always accurate.',
	},
	navSectionsLabel: {
		id: 'twg-microsite.navbar.navSectionsLabel',
		defaultMessage: 'Sections of {page}',
		description:
			'Accessible name for the small arrow button that opens the section list for a navigation link. The button shows only an arrow, no text, so this names what it opens. {page} resolves to the label of the link the button sits beside, for example "About".',
	},
	profileLabel: {
		id: 'twg-microsite.navbar.profileLabel',
		defaultMessage: 'Profile',
		description: 'Label and icon label for the Profile menu item in the user dropdown',
	},
	logoutLabel: {
		id: 'twg-microsite.navbar.logoutLabel',
		defaultMessage: 'Logout',
		description: 'Label and icon label for the Logout menu item in the user dropdown',
	},
	giveFeedback: {
		id: 'twg-microsite.navbar.giveFeedback',
		defaultMessage: 'Give feedback',
		description:
			'Label for the navigation bar button, menu item, and user menu item that open the feedback form.',
	},
	logoAlt: {
		id: 'twg-microsite.navbar.logoAlt',
		defaultMessage: 'Teamwork Graph logo',
		description:
			'Accessible label for the Teamwork Graph logo. "Teamwork Graph" is an Atlassian product name.',
	},
});
